import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthenticatedVideo, isObjectStorageUrl } from '../../hooks/useAuthenticatedFile';
import {
  ArrowLeft,
  BookOpen,
  Play,
  FileText,
  ExternalLink,
  Clock,
  CheckCircle,
  Circle,
  ChevronRight,
  Video,
  File,
  Link as LinkIcon,
  Award,
  HelpCircle,
  Loader2,
  Eye,
  X
} from 'lucide-react';

export default function CoursePreview() {
  const { courseId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [activeSubModule, setActiveSubModule] = useState(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  
  // Simulated progress (for preview purposes)
  const [simulatedProgress, setSimulatedProgress] = useState({});

  const fetchCourse = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tech-learning/builder/courses/${courseId}/preview`);
      setCourse(response.data.course);
      
      // Set first module as active by default
      if (response.data.course?.modules?.length > 0) {
        setActiveModule(response.data.course.modules[0]);
        if (response.data.course.modules[0].sub_modules?.length > 0) {
          setActiveSubModule(response.data.course.modules[0].sub_modules[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch course preview:', error);
      toast.error('Failed to load course preview');
      navigate('/admin/course-builder');
    } finally {
      setLoading(false);
    }
  }, [api, courseId, navigate]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  const getContentCount = (target) => {
    const content = target?.content || {};
    return (
      (content.videos?.length || 0) +
      (content.documents?.length || 0) +
      (content.onedrive_links?.length || 0)
    );
  };

  const handlePlayVideo = (video) => {
    setCurrentVideo(video);
    setShowVideoPlayer(true);
  };

  const handleTakeQuiz = (assessment) => {
    setCurrentQuiz(assessment);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
    setShowQuizDialog(true);
  };

  const handleSubmitQuiz = () => {
    if (!currentQuiz) return;
    
    let correct = 0;
    const total = currentQuiz.questions?.length || 0;
    
    currentQuiz.questions?.forEach(q => {
      const answer = quizAnswers[q.id];
      if (q.type === 'mcq') {
        const correctOption = q.options?.find(o => o.is_correct);
        if (answer === correctOption?.id) correct++;
      } else if (q.type === 'true_false') {
        if (answer === q.correct_answer) correct++;
      }
    });
    
    const score = Math.round((correct / total) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
  };

  const markAsViewed = (contentId, contentType) => {
    setSimulatedProgress(prev => ({
      ...prev,
      [contentId]: { viewed: true, type: contentType }
    }));
    toast.success('Content marked as viewed (preview mode)');
  };

  const renderVideoEmbed = (video) => {
    const url = video.url || '';
    
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop()?.split('?')[0]
        : new URLSearchParams(new URL(url).search).get('v');
      return (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          className="w-full aspect-video rounded-lg"
          allowFullScreen
          title={video.title}
        />
      );
    }
    
    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop()?.split('?')[0];
      return (
        <iframe
          src={`https://player.vimeo.com/video/${videoId}`}
          className="w-full aspect-video rounded-lg"
          allowFullScreen
          title={video.title}
        />
      );
    }
    
    // Direct video file
    if (url.startsWith('/uploads/') || url.startsWith('/api/tech-learning/builder/files/') || url.endsWith('.mp4') || url.endsWith('.webm')) {
      // Use AuthenticatedVideo for object storage URLs
      if (isObjectStorageUrl(url)) {
        return (
          <AuthenticatedVideo
            src={url}
            controls
            className="w-full aspect-video rounded-lg bg-black"
          />
        );
      }
      // Direct URL for legacy uploads
      const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;
      return (
        <video
          src={fullUrl}
          controls
          className="w-full aspect-video rounded-lg bg-black"
        >
          Your browser does not support video playback.
        </video>
      );
    }
    
    // Fallback - external link
    return (
      <div className="w-full aspect-video rounded-lg bg-muted flex flex-col items-center justify-center gap-4">
        <Video className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">External video</p>
        <Button asChild variant="outline">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-2" />
            Open in New Tab
          </a>
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!course) {
    return (
      <MainLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold">Course not found</h2>
          <Button asChild className="mt-4">
            <Link to="/admin/course-builder">Back to Courses</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="course-preview">
        {/* Preview Banner */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Preview Mode</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                This is how learners will see the course. Progress is simulated.
              </p>
            </div>
          </div>
          <Button variant="outline" asChild>
            <Link to={`/admin/course-builder/${courseId}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Editor
            </Link>
          </Button>
        </div>

        {/* Course Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {course.thumbnail_url ? (
                <img 
                  src={course.thumbnail_url} 
                  alt={course.title}
                  className="w-full md:w-64 h-40 object-cover rounded-lg"
                />
              ) : (
                <div className="w-full md:w-64 h-40 bg-gradient-to-br from-primary/20 to-primary/5 rounded-lg flex items-center justify-center">
                  <BookOpen className="h-12 w-12 text-primary/50" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{course.category_name}</Badge>
                  {course.status === 'published' && (
                    <Badge className="bg-emerald-500">Published</Badge>
                  )}
                </div>
                <h1 className="text-2xl font-bold">{course.title}</h1>
                <p className="text-muted-foreground mt-2">{course.description}</p>
                
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-1">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span>{course.preview_stats?.total_modules || 0} modules</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Video className="h-4 w-4 text-muted-foreground" />
                    <span>{course.total_videos || 0} videos</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span>{course.total_documents || 0} documents</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>~{course.preview_stats?.estimated_duration_minutes || 0} min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    <span>{course.passing_score}% to pass</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Course Outline (Left) */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Course Outline</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <Accordion type="single" collapsible className="px-4 pb-4">
                  {course.modules?.map((module, mIdx) => (
                    <AccordionItem key={module.id} value={module.id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-2 text-left">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                            activeModule?.id === module.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                          }`}>
                            {mIdx + 1}
                          </div>
                          <span className="flex-1 truncate">{module.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {module.has_sub_modules ? (
                          <div className="space-y-1 pl-8">
                            {module.sub_modules?.map((sm, smIdx) => (
                              <button
                                key={sm.id}
                                onClick={() => {
                                  setActiveModule(module);
                                  setActiveSubModule(sm);
                                }}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                                  activeSubModule?.id === sm.id 
                                    ? 'bg-primary/10 text-primary' 
                                    : 'hover:bg-muted'
                                }`}
                              >
                                {simulatedProgress[sm.id] ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                ) : (
                                  <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                )}
                                <span className="truncate">{sm.title}</span>
                                <span className="text-xs text-muted-foreground ml-auto">
                                  {getContentCount(sm)}
                                </span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setActiveModule(module);
                              setActiveSubModule(null);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ml-8 ${
                              activeModule?.id === module.id && !activeSubModule
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-muted'
                            }`}
                          >
                            <FileText className="h-4 w-4" />
                            <span>View Content ({getContentCount(module)})</span>
                          </button>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Content Area (Right) */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>
                {activeSubModule?.title || activeModule?.title || 'Select a module'}
              </CardTitle>
              {(activeSubModule?.description || activeModule?.description) && (
                <CardDescription>
                  {activeSubModule?.description || activeModule?.description}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {activeSubModule || (activeModule && !activeModule.has_sub_modules) ? (
                <div className="space-y-6">
                  {/* Content List */}
                  {(() => {
                    const target = activeSubModule || activeModule;
                    const content = target?.content || {};
                    const videos = content.videos || [];
                    const documents = content.documents || [];
                    const links = content.onedrive_links || [];
                    
                    if (videos.length === 0 && documents.length === 0 && links.length === 0) {
                      return (
                        <div className="text-center py-12 text-muted-foreground">
                          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>No content in this section</p>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        {/* Videos */}
                        {videos.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <Video className="h-4 w-4" />
                              Videos ({videos.length})
                            </h4>
                            <div className="space-y-2">
                              {videos.map(video => (
                                <div 
                                  key={video.id}
                                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {simulatedProgress[video.id] ? (
                                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                                    ) : (
                                      <Play className="h-5 w-5 text-primary" />
                                    )}
                                    <span>{video.title}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {video.type === 'video_upload' ? 'Video' : 'URL'}
                                    </Badge>
                                  </div>
                                  <Button size="sm" onClick={() => handlePlayVideo(video)}>
                                    Watch
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Documents */}
                        {documents.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <File className="h-4 w-4" />
                              Documents ({documents.length})
                            </h4>
                            <div className="space-y-2">
                              {documents.map(doc => (
                                <div 
                                  key={doc.id}
                                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {simulatedProgress[doc.id] ? (
                                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                                    ) : (
                                      <FileText className="h-5 w-5 text-orange-500" />
                                    )}
                                    <span>{doc.title}</span>
                                    <Badge variant="outline" className="text-xs">{doc.file_type}</Badge>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => markAsViewed(doc.id, 'document')}
                                    >
                                      Mark Read
                                    </Button>
                                    <Button size="sm" variant="outline" asChild>
                                      <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink className="h-4 w-4" />
                                      </a>
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* OneDrive Links */}
                        {links.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <LinkIcon className="h-4 w-4" />
                              OneDrive Resources ({links.length})
                            </h4>
                            <div className="space-y-2">
                              {links.map(link => (
                                <div 
                                  key={link.id}
                                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {simulatedProgress[link.id] ? (
                                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                                    ) : (
                                      <ExternalLink className="h-5 w-5 text-purple-500" />
                                    )}
                                    <div>
                                      <span>{link.title}</span>
                                      {link.description && (
                                        <p className="text-xs text-muted-foreground">{link.description}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => markAsViewed(link.id, 'link')}
                                    >
                                      Mark Read
                                    </Button>
                                    <Button size="sm" variant="outline" asChild>
                                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                                        Open
                                      </a>
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Assessment */}
                        {(activeSubModule?.assessment || activeModule?.assessment) && (
                          <div className="border-t pt-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                  <HelpCircle className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                  <h4 className="font-medium">Section Assessment</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {(activeSubModule?.assessment || activeModule?.assessment)?.questions?.length || 0} questions • 
                                    {(activeSubModule?.assessment || activeModule?.assessment)?.passing_score || 75}% to pass
                                  </p>
                                </div>
                              </div>
                              <Button onClick={() => handleTakeQuiz(activeSubModule?.assessment || activeModule?.assessment)}>
                                Take Quiz
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a module or sub-module from the outline</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Video Player Dialog */}
        <Dialog open={showVideoPlayer} onOpenChange={setShowVideoPlayer}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{currentVideo?.title}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {currentVideo && renderVideoEmbed(currentVideo)}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  markAsViewed(currentVideo?.id, 'video');
                  setShowVideoPlayer(false);
                }}
              >
                Mark as Watched & Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Dialog */}
        <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>{currentQuiz?.title || 'Assessment'}</DialogTitle>
              {!quizSubmitted && (
                <DialogDescription>
                  Answer all questions to complete this assessment
                </DialogDescription>
              )}
            </DialogHeader>
            <ScrollArea className="max-h-[50vh] pr-4">
              <div className="space-y-6 py-4">
                {quizSubmitted ? (
                  <div className="text-center py-8">
                    <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-3xl font-bold ${
                      quizScore >= (currentQuiz?.passing_score || 75) 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {quizScore}%
                    </div>
                    <h3 className="text-xl font-semibold mt-4">
                      {quizScore >= (currentQuiz?.passing_score || 75) ? 'Passed!' : 'Not Passed'}
                    </h3>
                    <p className="text-muted-foreground mt-2">
                      {quizScore >= (currentQuiz?.passing_score || 75) 
                        ? 'Great job! You passed this assessment.'
                        : `You need ${currentQuiz?.passing_score || 75}% to pass. Try again!`
                      }
                    </p>
                  </div>
                ) : (
                  currentQuiz?.questions?.map((q, idx) => (
                    <div key={q.id} className="space-y-3">
                      <div className="flex items-start gap-2">
                        <span className="font-medium min-w-[24px]">{idx + 1}.</span>
                        <div className="flex-1">
                          <p className="font-medium">{q.question}</p>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {q.type === 'mcq' ? 'Multiple Choice' : q.type === 'true_false' ? 'True/False' : 'Coding'}
                          </Badge>
                        </div>
                      </div>
                      
                      {q.type === 'mcq' && (
                        <RadioGroup
                          value={quizAnswers[q.id] || ''}
                          onValueChange={(v) => setQuizAnswers({ ...quizAnswers, [q.id]: v })}
                          className="pl-8"
                        >
                          {q.options?.map(opt => (
                            <div key={opt.id} className="flex items-center space-x-2">
                              <RadioGroupItem value={opt.id} id={`${q.id}-${opt.id}`} />
                              <Label htmlFor={`${q.id}-${opt.id}`}>{opt.text}</Label>
                            </div>
                          ))}
                        </RadioGroup>
                      )}
                      
                      {q.type === 'true_false' && (
                        <RadioGroup
                          value={quizAnswers[q.id] || ''}
                          onValueChange={(v) => setQuizAnswers({ ...quizAnswers, [q.id]: v })}
                          className="pl-8"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="true" id={`${q.id}-true`} />
                            <Label htmlFor={`${q.id}-true`}>True</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="false" id={`${q.id}-false`} />
                            <Label htmlFor={`${q.id}-false`}>False</Label>
                          </div>
                        </RadioGroup>
                      )}
                      
                      {q.type === 'coding' && (
                        <div className="pl-8">
                          <pre className="bg-muted p-3 rounded text-sm overflow-x-auto">
                            {q.code_template}
                          </pre>
                          <p className="text-sm text-muted-foreground mt-2">
                            Expected: {q.expected_output}
                          </p>
                          <p className="text-sm text-amber-600 mt-1">
                            (Coding questions are auto-marked as correct in preview mode)
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            <DialogFooter>
              {quizSubmitted ? (
                <Button onClick={() => setShowQuizDialog(false)}>Close</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setShowQuizDialog(false)}>Cancel</Button>
                  <Button onClick={handleSubmitQuiz}>Submit Quiz</Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
